import React from 'react';
import { Link } from 'react-router-dom';
import './IndexLanding.css';

export const IndexLanding: React.FC = () => {
  return (
    <div className="idxl-page">
      {/* ---- Nav ---- */}
      <nav className="idxl-nav">
        <Link to="/" className="idxl-nav-brand">
          <div className="idxl-nav-logo">IDX</div>
          <span className="idxl-nav-title">Index Options</span>
        </Link>
        <div className="idxl-nav-actions">
          <Link to="/" className="idxl-nav-link">Home</Link>
          <Link to="/login" className="idxl-btn idxl-btn-primary">Get Started</Link>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="idxl-hero">
        <div className="idxl-hero-badge">AI-POWERED INDEX OPTIONS INTELLIGENCE</div>
        <h1>Find the Best Index Put to Sell. Every Day.</h1>
        <p className="idxl-hero-subtitle">
          Our system analyzes hundreds of index put options across SPY, ES, and MES,
          scores them with a proprietary quantitative model, then validates the top pick
          with two independent AI engines — so you trade with confidence, not guesswork.
        </p>
        <div className="idxl-hero-actions">
          <Link to="/register" className="idxl-btn idxl-btn-primary idxl-btn-lg">
            Start Free Trial
          </Link>
          <a href="#how-it-works" className="idxl-btn idxl-btn-outline idxl-btn-lg">
            See How It Works
          </a>
        </div>
      </section>

      {/* ---- Supported Symbols ---- */}
      <div className="idxl-symbols">
        <div className="idxl-symbol-card idxl-symbol-card-active">
          <div className="idxl-symbol-name">SPY</div>
          <div className="idxl-symbol-desc">S&P 500 ETF Options</div>
        </div>
        <div className="idxl-symbol-card idxl-symbol-card-active">
          <div className="idxl-symbol-name">ES</div>
          <div className="idxl-symbol-desc">E-mini S&P 500 Futures</div>
        </div>
        <div className="idxl-symbol-card idxl-symbol-card-active">
          <div className="idxl-symbol-name">MES</div>
          <div className="idxl-symbol-desc">Micro E-mini S&P 500</div>
        </div>
      </div>

      {/* ---- Stats Strip ---- */}
      <section className="idxl-stats">
        <div className="idxl-stat">
          <div className="idxl-stat-val">500+</div>
          <div className="idxl-stat-label">Put Options Analyzed</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">3x</div>
          <div className="idxl-stat-label">Daily Snapshots</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">3</div>
          <div className="idxl-stat-label">Independent Validations</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">6</div>
          <div className="idxl-stat-label">Scoring Factors</div>
        </div>
      </section>

      {/* ---- How It Works ---- */}
      <section className="idxl-section" id="how-it-works">
        <div className="idxl-section-label">How It Works</div>
        <h2 className="idxl-section-title">From Raw Data to High-Confidence Trades</h2>
        <p className="idxl-section-desc">
          Three times per trading day, our pipeline collects live market data,
          runs quantitative analysis, and delivers an AI-validated recommendation —
          all in under 15 minutes.
        </p>

        <div className="idxl-pipeline">
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num">1</div>
            <div className="idxl-pipe-title">Live Data Collection</div>
            <div className="idxl-pipe-desc">
              Real-time Greeks for every index put option — delta, theta, vega,
              IV, and more — captured directly from the brokerage via persistent
              WebSocket connection.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num">2</div>
            <div className="idxl-pipe-title">Quantitative Scoring</div>
            <div className="idxl-pipe-desc">
              A proprietary multi-factor model scores every candidate on
              risk-adjusted yield, safety cushion, probability of profit,
              and time decay — grouped by time horizon for fair comparison.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num">3</div>
            <div className="idxl-pipe-title">Dual AI Validation</div>
            <div className="idxl-pipe-desc">
              The top candidates are sent to two leading AI systems —
              Claude (Anthropic) and ChatGPT (OpenAI) — for independent
              analysis. Neither sees our rankings.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num">4</div>
            <div className="idxl-pipe-title">Confidence Score</div>
            <div className="idxl-pipe-desc">
              We compare all three rankings. When our math, Claude, and ChatGPT
              agree — you get a HIGH confidence read. Disagreement triggers
              human review.
            </div>
          </div>
        </div>
      </section>

      {/* ---- Features ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">What You Get</div>
        <h2 className="idxl-section-title">Everything You Need to Trade Index Puts</h2>
        <p className="idxl-section-desc">
          The dashboard delivers actionable intelligence — not raw data.
          Every read comes with full transparency so you understand exactly
          why a trade was recommended.
        </p>

        <div className="idxl-features">
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#9889;</div>
            <div className="idxl-feat-title">Three Best Trade Cards</div>
            <div className="idxl-feat-desc">
              Side-by-side #1 picks from our model, Claude AI, and ChatGPT —
              with identical metrics so you can compare at a glance. Strike,
              expiration, credit, probability of profit, and more.
            </div>
          </div>
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#128202;</div>
            <div className="idxl-feat-title">DTE Bucket Rankings</div>
            <div className="idxl-feat-desc">
              Candidates ranked within time horizons (weekly, bi-weekly,
              monthly) so short-term and long-term trades compete fairly.
              Expand any bucket to see the full ranked table.
            </div>
          </div>
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#129302;</div>
            <div className="idxl-feat-title">AI Reasoning & Context</div>
            <div className="idxl-feat-desc">
              Read the AI's reasoning for every ranking. See exactly what
              qualitative factors — weekend risk, execution risk, term
              structure — influenced each pick.
            </div>
          </div>
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#128200;</div>
            <div className="idxl-feat-title">Multi-Symbol Support</div>
            <div className="idxl-feat-desc">
              Select from multiple index products — SPY, ES (E-mini S&P 500),
              and MES (Micro E-mini). Separate dashboards for equity and
              futures options, all your put-selling research organized by product.
            </div>
          </div>
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#128196;</div>
            <div className="idxl-feat-title">Excel Reports</div>
            <div className="idxl-feat-desc">
              Download the full analysis as a formatted Excel workbook —
              enhanced rankings, AI top picks with reasoning, and best
              trades per time horizon. Perfect for your records.
            </div>
          </div>
          <div className="idxl-feat">
            <div className="idxl-feat-icon">&#128337;</div>
            <div className="idxl-feat-title">Historical Picks</div>
            <div className="idxl-feat-desc">
              Browse past picks by date to review accuracy over time.
              Every snapshot is stored with full rankings, AI analysis,
              and confidence scores for complete auditability.
            </div>
          </div>
        </div>
      </section>

      {/* ---- Tri-Validation ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">Triple Validation</div>
        <h2 className="idxl-section-title">Three Independent Minds. One Recommendation.</h2>
        <p className="idxl-section-desc">
          No single model makes the decision alone. Our quantitative engine provides
          mathematical rigor. Two separate AI systems provide independent judgment.
          Agreement across all three dramatically reduces the chance of error.
        </p>

        <div className="idxl-tri-grid">
          <div className="idxl-tri-card idxl-tri-card-model">
            <div className="idxl-tri-icon">&#128201;</div>
            <div className="idxl-tri-name">Our Quantitative Model</div>
            <div className="idxl-tri-desc">
              Multi-factor scoring engine that evaluates yield efficiency,
              safety cushion, probability of profit, time decay, and volatility
              exposure. Candidates are ranked within peer groups for fair comparison.
            </div>
          </div>
          <div className="idxl-tri-card idxl-tri-card-claude">
            <div className="idxl-tri-icon">&#129504;</div>
            <div className="idxl-tri-name">Claude AI (Anthropic)</div>
            <div className="idxl-tri-desc">
              Anthropic's advanced reasoning model analyzes the raw data
              independently — identifying the single best trade and ranking
              the top candidates with detailed qualitative reasoning.
            </div>
          </div>
          <div className="idxl-tri-card idxl-tri-card-gpt">
            <div className="idxl-tri-icon">&#128640;</div>
            <div className="idxl-tri-name">ChatGPT (OpenAI)</div>
            <div className="idxl-tri-desc">
              OpenAI's leading AI independently evaluates the same data,
              providing a second AI perspective on the best trade with its
              own risk assessment and market context analysis.
            </div>
          </div>
        </div>
      </section>

      {/* ---- Confidence Scoring ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">Confidence Framework</div>
        <h2 className="idxl-section-title">Know How Much to Trust Each Pick</h2>
        <p className="idxl-section-desc">
          The system compares all three rankings and assigns a confidence level
          to every pick. When math and AI agree, you trade with conviction.
          When they diverge, you investigate before acting.
        </p>

        <table className="idxl-conf-table">
          <thead>
            <tr>
              <th>Agreement</th>
              <th>Confidence</th>
              <th>What It Means</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>All three sources agree on #1 pick</td>
              <td><span className="idxl-badge idxl-badge-high">HIGH</span></td>
              <td>Strong consensus — both quantitative math and AI reasoning converge</td>
            </tr>
            <tr>
              <td>Rankings differ by 1-2 positions</td>
              <td><span className="idxl-badge idxl-badge-med">MEDIUM</span></td>
              <td>Close agreement — minor weighting differences between approaches</td>
            </tr>
            <tr>
              <td>Rankings differ by 3+ positions</td>
              <td><span className="idxl-badge idxl-badge-low">LOW</span></td>
              <td>Divergence — warrants investigation before trading</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ---- CTA ---- */}
      <section className="idxl-cta">
        <h2>Ready to Trade Index Options Smarter?</h2>
        <p>
          Join traders who rely on triple-validated research — not gut feelings —
          to sell index puts with confidence.
        </p>
        <div className="idxl-hero-actions">
          <Link to="/register" className="idxl-btn idxl-btn-primary idxl-btn-lg">
            Start Your Free Trial
          </Link>
          <Link to="/login" className="idxl-btn idxl-btn-outline idxl-btn-lg">
            Sign In
          </Link>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="idxl-footer">
        Index Options Research is designed for educational and analytical purposes. All trading involves risk.
        Past research performance does not guarantee future results.
      </footer>
    </div>
  );
};
